/** What the recipe editor works on: a new recipe, one being edited, or an import. */
import type { Confidence, RecipeInput, RecipeView } from '../../../shared/api';
import type { Serving } from '../../../shared/foods';
import { parseDecimal } from '../../../shared/numbers';
import { BUILTIN_BY_KEY, formatAmount, type NutrientMap } from '../../../shared/nutrients';
import { fitServings, IMPORT_LIMITS, stepsToNotes, type ImportedRecipe, type ImportOrigin, type SiteNutrition } from '../../../shared/recipeImport';
import { MAX_ENTRY_GRAMS, RECIPE_LIMITS, safeHttpUrl } from '../../../shared/recipes';

/** How the AI matched an imported ingredient, flagged the way the Log sheet's review items are. */
export interface IngredientReview {
  confidence: Confidence;
  /** Nutrition from an AI estimate rather than a known food. */
  estimated: boolean;
  /** The known food it was matched to, when that name differs. */
  matchName: string | null;
  note: string | null;
}

export interface DraftIngredient {
  /** For list rendering only. */
  key: string;
  foodRef: string | null;
  name: string;
  brand: string | null;
  grams: number;
  per100: NutrientMap;
  servings: Serving[];
  /** Imported ingredients: the line it came from ("2 cups all-purpose flour"). It stays when the person picks another food. */
  line?: string;
  /** Imported ingredients: where the line was in the recipe, so a line matched later goes back in its place. */
  position?: number;
  /** How the AI matched an imported ingredient, until the person picks another food. */
  review?: IngredientReview;
}

/** An imported line that isn't matched to a food yet: the person picks one, or removes the line. */
export interface UnmatchedLine {
  key: string;
  line: string;
  /** Grams from an explicit weight in the line ("200 g"), to start its amount from. */
  gramsHint: number | null;
  /** Where the line was in the recipe. */
  position: number;
}

/** The ingredients with `matched` back in its recipe's order: before the first imported one that came after it. */
export function insertInPlace(list: readonly DraftIngredient[], matched: DraftIngredient): DraftIngredient[] {
  const at = matched.position === undefined ? -1 : list.findIndex((i) => i.position !== undefined && i.position > matched.position!);
  return at === -1 ? [...list, matched] : [...list.slice(0, at), matched, ...list.slice(at)];
}

/** What the editor shows about an import, beside the draft itself. */
export interface ImportInfo {
  origin: ImportOrigin;
  /** The site it came from ("bbcgoodfood.com"), for links. */
  site: string | null;
  /** The site's own nutrition per serving, shown next to Grams' numbers as a check. */
  siteNutrition: SiteNutrition | null;
  /** Why the ingredients aren't matched yet (AI off, say), or null. */
  notice: string | null;
  /** The yield as the source wrote it ("Makes 12 cookies"). */
  yieldText: string | null;
  /** Steps that didn't fit in the notes (they're cut at whole steps). */
  stepsLeft: number;
}

export interface RecipeDraft {
  name: string;
  /** Null until chosen: a new recipe asks how many servings it makes. */
  servings: number | null;
  cookedGrams: number | null;
  notes: string;
  /** The page it came from. The editor doesn't show it; saving keeps it. */
  sourceUrl: string | null;
  ingredients: DraftIngredient[];
  /** Imported lines still to match to a food. */
  unmatched?: UnmatchedLine[];
  /** Set when the draft came from an import. */
  imported?: ImportInfo;
}

let seq = 0;
export const ingredientKey = () => `ingredient-${++seq}`;

/** "www.bbcgoodfood.com" → "bbcgoodfood.com", for "From …". */
export function siteOf(link: string | null): string | null {
  const href = link ? safeHttpUrl(link) : null;
  return href ? new URL(href).hostname.replace(/^www\./, '') : null;
}

/** Punctuation that ends a sentence or a quote, in Latin and CJK text: never the end of a link. */
const LINK_END = /[.,;:!?'"\]>“”‘’«»‹›…。，、；：！？）】」』]+$/u;

/** "…/Chili_(food))." → "…/Chili_(food)": punctuation after a link in a sentence isn't part of it. */
function trimLinkEnd(link: string): string {
  let out = link.replace(LINK_END, '');
  while (out.endsWith(')') && out.split('(').length < out.split(')').length) out = out.slice(0, -1).replace(LINK_END, '');
  return out;
}

/**
 * The link to import from what was typed or pasted: "bbcgoodfood.com/…" gets https://,
 * and shared text ("Try this: “https://…”") gives its first link. Null when there's none.
 */
export function importLink(text: string): string | null {
  const t = text.trim();
  // A link ends at a space, a quote or CJK punctuation (text often follows a link with no space in Chinese or Japanese).
  const shared = /https?:\/\/[^\s<>"“”‘’«»…。，、；！？（）【】「」『』]+/iu.exec(t)?.[0];
  let candidate: string | null = null;
  if (shared) candidate = trimLinkEnd(shared);
  // Typed without https://, it has to name a website: "1.5" would read as the address 1.0.0.5.
  else if (t !== '' && !/\s/.test(t)) candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(t) ? t : /^[^/?#]*\p{L}/u.test(t) ? `https://${t}` : null;
  const href = candidate ? safeHttpUrl(candidate) : null;
  if (!href || href.length > IMPORT_LIMITS.url) return null;
  // A name without a dot ("chili") isn't a website.
  return new URL(href).hostname.includes('.') ? href : null;
}

/**
 * An import as an editor draft: matched ingredients with their review flags, the lines
 * still to match, the steps as notes, and the site's own nutrition to compare with.
 * Kept within what a recipe can hold, so saving can't fail on it.
 */
export function draftFromImport(imported: ImportedRecipe): RecipeDraft {
  const ingredients: DraftIngredient[] = [];
  const unmatched: UnmatchedLine[] = [];
  for (const [position, { line, item, gramsHint }] of imported.ingredients.slice(0, IMPORT_LIMITS.ingredients).entries()) {
    if (!item) {
      unmatched.push({ key: ingredientKey(), line, gramsHint, position });
      continue;
    }
    ingredients.push({
      key: ingredientKey(),
      foodRef: item.foodRef,
      name: item.name.slice(0, RECIPE_LIMITS.name),
      brand: item.brand?.slice(0, 80) ?? null,
      grams: Math.min(MAX_ENTRY_GRAMS, item.grams),
      per100: item.per100,
      servings: item.servings,
      line,
      position,
      review: { confidence: item.confidence, estimated: item.estimated, matchName: item.matchName, note: item.note },
    });
  }
  const { notes, left } = stepsToNotes(imported.steps);
  // The editor doesn't show the link, so one a recipe can't keep would make saving fail with no way to fix it.
  const link = imported.sourceUrl ? safeHttpUrl(imported.sourceUrl) : null;
  return {
    name: imported.name.slice(0, RECIPE_LIMITS.name),
    servings: fitServings(imported.servings),
    cookedGrams: null,
    notes,
    sourceUrl: link && link.length <= RECIPE_LIMITS.sourceUrl ? link : null,
    ingredients,
    unmatched,
    imported: {
      origin: imported.origin,
      site: siteOf(imported.sourceUrl),
      siteNutrition: imported.siteNutrition,
      notice: imported.notice,
      yieldText: imported.yieldText,
      stepsLeft: left,
    },
  };
}

/** Whether an ingredient needs a second look: the AI wasn't sure, or its nutrition is an estimate. */
export function needsCheck(review: IngredientReview | undefined): boolean {
  return review !== undefined && (review.confidence === 'low' || review.estimated);
}

export interface ComparisonRow {
  key: string;
  label: string;
  /** "312 kcal", "28 g". */
  site: string;
  /** The ingredients' number for one serving, or "—" (no ingredients or servings yet, or none has it). */
  ours: string;
  /** Only some ingredients have it, so the real amount may be higher. */
  partial: boolean;
}

/** One serving of the ingredients so far, with how many of them had each nutrient. */
export interface OurServing {
  perServing: NutrientMap;
  known: Record<string, number>;
  count: number;
}

/**
 * The nutrients the site published, beside the ingredients' numbers for one serving, in the
 * order extraction reads them (calories, then macros, then the rest). `linesLeft`: imported
 * lines not matched yet, which the ingredients' numbers can't include.
 */
export function siteComparison(site: NutrientMap, ours: OurServing | null, linesLeft = 0): ComparisonRow[] {
  const rows: ComparisonRow[] = [];
  for (const [key, value] of Object.entries(site)) {
    const def = BUILTIN_BY_KEY.get(key);
    if (!def) continue;
    const show = (n: number | undefined) => (n === undefined ? '—' : `${formatAmount(n, def)} ${def.unit}`);
    // No ingredient with a value for it: unknown, not zero.
    const mine = ours ? ours.perServing[key] : undefined;
    const known = ours && Object.hasOwn(ours.known, key) ? ours.known[key]! : 0;
    const partial = mine !== undefined && ours !== null && (known < ours.count || linesLeft > 0);
    rows.push({ key, label: def.name, site: show(value), ours: show(mine), partial });
  }
  return rows;
}

/** Starting from scratch. */
export function emptyDraft(): RecipeDraft {
  return { name: '', servings: null, cookedGrams: null, notes: '', sourceUrl: null, ingredients: [] };
}

export function draftFrom(recipe: RecipeView): RecipeDraft {
  return {
    name: recipe.name,
    servings: recipe.servings,
    cookedGrams: recipe.cookedGrams,
    notes: recipe.notes,
    sourceUrl: recipe.sourceUrl,
    ingredients: recipe.ingredients.map((i) => ({
      key: ingredientKey(),
      foodRef: i.foodRef,
      name: i.name,
      brand: i.brand,
      grams: i.grams,
      per100: i.per100,
      servings: i.servings,
    })),
  };
}

/** Servings as typed: a whole number within the limits (digits only, so "1e2" or "0x10" aren't 100 and 16), else null. */
export function parseServings(text: string): number | null {
  const t = text.trim();
  if (!/^\d{1,3}$/.test(t)) return null;
  const n = Number(t);
  return n >= RECIPE_LIMITS.servings.min && n <= RECIPE_LIMITS.servings.max ? n : null;
}

/** The cooked weight as typed. Empty is fine (no cooked weight); anything else must be grams within the limits. */
export function parseCooked(text: string): { grams: number | null; valid: boolean } {
  if (text.trim() === '') return { grams: null, valid: true };
  const n = parseDecimal(text);
  return n !== null && n >= 1 && n <= RECIPE_LIMITS.cookedGrams ? { grams: n, valid: true } : { grams: null, valid: false };
}

/** The request body for saving a draft whose servings were checked. */
export function toInput(draft: RecipeDraft & { servings: number }): RecipeInput {
  return {
    name: draft.name.trim(),
    servings: draft.servings,
    cookedGrams: draft.cookedGrams,
    notes: draft.notes.trim(),
    sourceUrl: draft.sourceUrl,
    ingredients: draft.ingredients.map(({ foodRef, name, brand, grams, per100, servings }) => ({ foodRef, name, brand, grams, per100, servings })),
  };
}
